import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, MessageSquare, ClipboardCheck, Rocket, ShieldCheck, Clock, PoundSterling, HeartHandshake } from "lucide-react";
import Seo from "../components/Seo";
import BookCallButton from "../components/BookCallButton";
import GoogleRatingBadge from "../components/GoogleRatingBadge";

const STEPS = [
  { icon: MessageSquare, title: "You say hello", text: "Book a free, no-obligation call. We'll understand your business, where you are now and where you want to get to — and confirm exactly how we can help." },
  { icon: ClipboardCheck, title: "We handle the handover", text: "With your permission, we contact your current accountant, request your records under professional clearance and take care of the paperwork. You barely have to lift a finger." },
  { icon: Rocket, title: "You're up and running", text: "We get you set up on Xero, agree a simple monthly plan, and start giving you the proactive support and clarity you've been missing — more than numbers, from day one." },
];

const REASSURE = [
  { icon: ShieldCheck, title: "We do the heavy lifting", text: "Professional clearance, record transfers, HMRC agent authorisations — we manage the lot on your behalf." },
  { icon: Clock, title: "There's no bad time to switch", text: "You can move to CASS at any point in your financial year. We'll make sure nothing is missed in the transition." },
  { icon: PoundSterling, title: "No nasty surprises", text: "Clear, fixed monthly pricing agreed up front. No hidden fees for switching, and no long tie-ins." },
  { icon: HeartHandshake, title: "A genuine relationship", text: "You get a proactive finance team who actually pick up the phone — not just a once-a-year set of accounts." },
];

const FAQS = [
  { q: "Is switching accountants a lot of hassle?", a: "No — it's one of the easiest things you'll do. Once you give us the go-ahead, we contact your existing accountant, request everything we need and handle the professional clearance process for you. Most clients are surprised how little they have to do." },
  { q: "Will there be a gap in my accounting?", a: "No. We plan the handover so your records, deadlines and filings are all accounted for. Nothing falls through the cracks between the old and the new." },
  { q: "Do I have to tell my current accountant?", a: "You'll need to let them know you're moving, but we can guide you on exactly what to say — a short, polite email is usually all it takes. We then deal with them directly for the technical handover." },
  { q: "When is the best time to switch?", a: "Whenever suits you. Many people switch after a year-end or at the start of a new financial year, but you can move at any time and we'll make it seamless." },
];

export default function SwitchingAccountants() {
  return (
    <div data-testid="switching-page">
      <Seo title="Switching to CASS Is Easy" description="Thinking of changing accountant? Switching to CASS is simple — we handle the handover, professional clearance and paperwork for you. A stress-free move to a proactive finance team." />

      {/* Hero */}
      <section className="bg-navy-deep text-white py-16 md:py-24">
        <div className="max-w-4xl mx-auto px-6">
          <span className="inline-flex items-center gap-2 bg-white/10 text-white text-xs font-semibold px-4 py-1.5 rounded-full mb-6"><HeartHandshake size={14} /> Making the move simple</span>
          <h1 className="font-display font-extrabold text-4xl md:text-5xl lg:text-6xl leading-tight mb-6">Switching to CASS is easy</h1>
          <p className="text-slate-300 text-lg max-w-2xl leading-relaxed">
            Feeling underwhelmed by your current accountant? Moving to CASS is far simpler than most people expect. We handle the entire handover for you — so you can get on with running your business.
          </p>
          <div className="flex flex-wrap items-center gap-4 mt-8">
            <BookCallButton testId="switching-hero-book" label="Book a call with Paul" className="inline-flex items-center gap-2 bg-orange hover:bg-orange-dark text-white font-bold px-8 py-4 rounded-full transition-all hover:-translate-y-0.5" />
            <GoogleRatingBadge dark />
          </div>
        </div>
      </section>

      {/* 3-step timeline */}
      <section className="py-14 md:py-20 bg-slate-50">
        <div className="max-w-5xl mx-auto px-6">
          <div className="text-center mb-12">
            <span className="text-xs font-bold uppercase tracking-widest text-orange">How it works</span>
            <h2 className="font-display font-extrabold text-3xl md:text-4xl text-navy mt-2">Three simple steps</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-6 relative">
            {STEPS.map((s, i) => {
              const Icon = s.icon;
              return (
                <div key={i} data-testid={`switch-step-${i}`} className="bg-white rounded-3xl border border-navy/5 shadow-sm p-7 relative">
                  <span className="absolute -top-4 left-7 w-9 h-9 rounded-full bg-orange text-white font-display font-extrabold flex items-center justify-center shadow-md">{i + 1}</span>
                  <div className="w-14 h-14 rounded-2xl bg-navy/5 text-navy flex items-center justify-center mb-5 mt-3"><Icon size={26} /></div>
                  <h3 className="font-display font-extrabold text-xl text-navy leading-snug mb-2">{s.title}</h3>
                  <p className="text-sm text-slate-500 leading-relaxed">{s.text}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Reassurance */}
      <section className="py-14 md:py-20 bg-white">
        <div className="max-w-5xl mx-auto px-6">
          <div className="text-center mb-12">
            <span className="text-xs font-bold uppercase tracking-widest text-orange">You're in safe hands</span>
            <h2 className="font-display font-extrabold text-3xl md:text-4xl text-navy mt-2">We handle everything</h2>
          </div>
          <div className="grid sm:grid-cols-2 gap-6">
            {REASSURE.map((r, i) => {
              const Icon = r.icon;
              return (
                <div key={i} data-testid={`switch-reassure-${i}`} className="flex gap-4 bg-slate-50 rounded-2xl p-6 border border-navy/5">
                  <span className="w-12 h-12 rounded-xl bg-teal/10 text-teal flex items-center justify-center shrink-0"><Icon size={22} /></span>
                  <div>
                    <p className="font-display font-bold text-navy">{r.title}</p>
                    <p className="text-sm text-slate-500 mt-1 leading-relaxed">{r.text}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-14 md:py-20 bg-slate-50">
        <div className="max-w-3xl mx-auto px-6">
          <h2 className="font-display font-extrabold text-3xl text-navy text-center mb-10">Common questions</h2>
          <div className="space-y-4">
            {FAQS.map((f, i) => (
              <div key={i} data-testid={`switch-faq-${i}`} className="bg-white rounded-2xl border border-navy/5 p-6">
                <p className="font-display font-bold text-navy mb-2">{f.q}</p>
                <p className="text-sm text-slate-500 leading-relaxed">{f.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 md:py-20 bg-navy-deep text-white">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <h2 className="font-display font-extrabold text-3xl md:text-4xl mb-4">Ready for a better accountant?</h2>
          <p className="text-slate-300 mb-8 max-w-xl mx-auto">Book a free, no-pressure call with Paul and see just how easy switching to CASS can be.</p>
          <div className="flex flex-wrap items-center justify-center gap-4">
            <BookCallButton testId="switching-cta-book" label="Book a call with Paul" className="inline-flex items-center gap-2 bg-orange hover:bg-orange-dark text-white font-bold px-8 py-4 rounded-full transition-all hover:-translate-y-0.5" />
            <Link to="/contact" data-testid="switching-cta-contact" className="inline-flex items-center gap-2 border-2 border-white/30 hover:border-white text-white font-bold px-8 py-4 rounded-full transition-all">Get in touch <ArrowRight size={18} /></Link>
          </div>
        </div>
      </section>
    </div>
  );
}
